import { ChangeDetectionStrategy, Component, DestroyRef, inject, signal, viewChild } from '@angular/core';
import { NavigationEnd, Router, RouterLink, RouterLinkActive } from '@angular/router';
import { MatSidenav, MatSidenavModule } from '@angular/material/sidenav';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { MatIcon } from '@angular/material/icon';
import { MatButton, MatFabButton, MatIconButton } from '@angular/material/button';
import { MatListModule } from '@angular/material/list';
import { filter } from 'rxjs';
import { PlatformDataService } from '@zhik/core/services/platform-data';
import { MortgageApplicationStore } from '@zhik/core/store/mortgage-journey/mortgage-application';
import { MenuComponent } from '../menu/menu.component';
import { FooterComponent } from '../footer/footer.component';

@Component({
  selector: 'dskhome-shell',
  templateUrl: './shell.component.html',
  styleUrls: ['./shell.component.scss'],
  imports: [
    MatListModule, MenuComponent, FooterComponent, MatSidenavModule, RouterLink, RouterLinkActive, MatIcon, MatButton, MatFabButton, MatIconButton
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ShellComponent {
  readonly platformDataService = inject(PlatformDataService);
  private readonly router = inject(Router);
  private readonly mortgageApplicationStore = inject(MortgageApplicationStore);
  private readonly destroyRef = inject(DestroyRef);

  sidenav = viewChild<MatSidenav>('snav');

  isSubmenuOpen = signal(false);
  isPurchaseSubmenuRouteActive = signal(false);

  protected readonly isMortgageApplicationPath = this.mortgageApplicationStore.isMortgageApplicationPath;

  private readonly saigeWidgetScriptSrc = process?.env['ZHIK_PUBLIC_SAIGE_WIDGET_SCRIPT_SRC'];

  constructor() {
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(() => {

      // TODO:Replace calculator with the final 'purchase' url
      this.closeSidenav();
      this.isPurchaseSubmenuRouteActive.set(this.router.url.startsWith('/calculator'));

      const journeyRoutes = [
        'mortgage-application/credit-requests',
        'preliminary-codebtor',
        'user-verification/verify-id-card',
        'user-verification/verify-phone-number',
      ];

      // TODO: we should create a service with the journey routes and use it here instead using the `mortgageApplicationStore`
      // `isMortgageApplicationPath` / `setIsMortgageApplicationPath` should be deleted from `mortgageApplicationStore`
      // for now this works because `mortgageApplicationStore` is provided in root
      // and we use `mortgageApplicationStore.isMortgageApplicationPath` only for this purpose
      if (journeyRoutes.some(route => this.router.url.includes(route))) {
        this.mortgageApplicationStore.setIsMortgageApplicationPath(true);
      } else {
        this.mortgageApplicationStore.setIsMortgageApplicationPath(false);
      }
    });

    if (this.platformDataService.isBrowser) {
      toObservable(this.platformDataService.windowWidthChange).pipe(
        takeUntilDestroyed(this.destroyRef)
      ).subscribe(() => {
        this.closeSidenav();
      });

      this.ensureSaigeWidgetScript();
    }
  }

  closeSidenav(): void {
    this.sidenav()?.close();
  }

  toggleSubMenu(): void {
    this.isSubmenuOpen.update(value => !value);
  }

  openCookieSettings(): void {
    this.platformDataService.window?.Cookiebot?.show();
  }

  openAiChat(): void {
    const browserWindow = this.platformDataService.window as (Window & {
      openAISearch?: () => void;
    }) | null;
    browserWindow?.openAISearch?.();
  }

  private ensureSaigeWidgetScript(): void {
    if (!this.saigeWidgetScriptSrc) {
      return;
    }

    const documentRef = this.platformDataService.documentRef;
    const existingScript = documentRef.querySelector(`script[src="${this.saigeWidgetScriptSrc}"]`) as HTMLScriptElement | null;

    if (existingScript) {
      return;
    }

    const script = documentRef.createElement('script');
    script.src = this.saigeWidgetScriptSrc;
    script.defer = true;
    documentRef.head.appendChild(script);
  }
}
